import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该文章不存在或已被删除</p>
            <Link
              to="/"
              className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-4">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors no-underline">
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-800 font-medium">
                {article.category}
              </span>
              <span>/</span>
              <span className="text-foreground-800 font-medium truncate max-w-[200px]">
                {article.title}
              </span>
            </nav>
          </div>
        </div>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </div>
                  {article.readTime}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-600">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  <span>{article.author}</span>
                </div>
              </div>
            </header>

            <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed">
              {article.content.split('\n\n').map((paragraph, idx) => {
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2
                      key={idx}
                      className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-8 mb-4"
                    >
                      {paragraph.replace('## ', '')}
                    </h2>
                  );
                }
                if (paragraph.startsWith('### ')) {
                  return (
                    <h3
                      key={idx}
                      className="text-lg font-heading font-bold text-foreground-900 mt-6 mb-3"
                    >
                      {paragraph.replace('### ', '')}
                    </h3>
                  );
                }
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return (
                    <p key={idx} className="text-sm font-semibold text-foreground-800 my-3">
                      <strong>{paragraph.replace(/\*\*/g, '')}</strong>
                    </p>
                  );
                }
                if (paragraph.startsWith('- ')) {
                  const items = paragraph.split('\n').filter((l) => l.startsWith('- '));
                  return (
                    <ul key={idx} className="list-disc pl-5 my-3 space-y-1 text-sm">
                      {items.map((item, i) => (
                        <li key={i} className="text-foreground-700">
                          {item.replace('- ', '')}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (paragraph === '---') {
                  return <hr key={idx} className="my-6 border-background-200/70" />;
                }
                return (
                  <p key={idx} className="text-sm text-foreground-700 leading-relaxed my-3">
                    {paragraph.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').split('<strong>').map((part, i) => {
                      if (part.includes('</strong>')) {
                        const [strong, rest] = part.split('</strong>');
                        return (
                          <span key={i}>
                            <strong>{strong}</strong>
                            {rest}
                          </span>
                        );
                      }
                      return <span key={i}>{part}</span>;
                    })}
                  </p>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-accent-100 text-accent-800 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="relative w-full h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading font-bold text-foreground-950 text-xs md:text-sm line-clamp-2 leading-snug">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 border border-background-300/60 text-foreground-700 rounded-full hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300 transition-all duration-300 text-sm font-medium whitespace-nowrap no-underline cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            image: article.image,
            publisher: {
              '@type': 'Organization',
              name: '火影同人游戏合集',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://naruto-fangame.com/article/${article.id}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://naruto-fangame.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.category,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}